import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createDeveloperUser,
  decideOrgRequest,
  deleteDeveloperUser,
  getDeveloperTrialOrg,
  getDeveloperUserOrgAccess,
  getOrgMembers,
  listClientManagerSummary,
  listDeveloperUsers,
  listPendingOrgRequests,
  setDeveloperTrialOrg,
  setDeveloperUserOrgAccess,
  updateDeveloperUser,
} from "../api/developerApi";
import { deleteOrganization, listOrganizations } from "../api/etlApi";
import { listTrialAccounts, deleteTrialAccount } from "../api/authApi";
import { formatCompactCurrency, formatNumber } from "../utils/formatters";
import {
  buildOrganizationDomainGroups,
  findOrganizationDomainKey,
  getOrganizationDomainLabel,
  organizationMatchesSearch,
} from "../utils/organizationDomains";

const EMPTY_USER_FORM = {
  email: "",
  username: "",
  full_name: "",
  role: "employee",
  is_active: true,
  can_create_org_without_approval: false,
  can_manage_employees: false,
  manager_email: "",
  assigned_orgs: [],
};

function getApiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  const message = error?.response?.data?.message;
  return detail || message || error?.message || fallback;
}

function formatDateTime(value) {
  if (!value) {
    return "-";
  }
  const dateValue = new Date(value);
  return Number.isNaN(dateValue.getTime()) ? "-" : dateValue.toLocaleString();
}

function formatRelativeDateTime(value) {
  if (!value) {
    return "";
  }
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) {
    return "";
  }

  const diffMs = Date.now() - dateValue.getTime();
  const diffMinutes = Math.max(0, Math.floor(diffMs / 60000));
  if (diffMinutes < 1) {
    return "Just now";
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} min ago`;
  }
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hr ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  }
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths} month${diffMonths === 1 ? "" : "s"} ago`;
}

function ModalShell({ title, subtitle, children, onClose, wide = false, footer }) {
  return (
    <div className="dev-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className={`dev-modal ${wide ? "wide" : ""}`.trim()}
        role="dialog"
        aria-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="dev-modal-head">
          <div>
            <div className="dev-modal-kicker">Admin Console</div>
            <h4>{title}</h4>
            {subtitle ? <p className="dev-modal-subtitle">{subtitle}</p> : null}
          </div>
          <button type="button" className="dev-icon-btn" onClick={onClose} aria-label="Close dialog">
            ×
          </button>
        </div>
        <div className="dev-modal-body">{children}</div>
        {footer ? <div className="dev-modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

function StatCard({ label, value, meta }) {
  return (
    <article className="dev-stat-card">
      <span className="dev-stat-label">{label}</span>
      <strong>{value}</strong>
      {meta ? <p>{meta}</p> : null}
    </article>
  );
}

function SectionHeader({ title, subtitle, actions }) {
  return (
    <div className="dev-section-head">
      <div>
        <h3>{title}</h3>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {actions ? <div className="dev-section-actions">{actions}</div> : null}
    </div>
  );
}

function RfmLogo({ size = 44 }) {
  // prefer the public asset used on the login slides so brand stays consistent
  return (
    <img src="/rfm-logo.ico" alt="KorvRFM" style={{ width: size, height: size }} />
  );
}

function AccessPill({ value }) {
  const className = value === "approved" ? "accent" : value === "blocked" ? "danger" : "muted";
  return <span className={`dev-pill ${className}`}>{value}</span>;
}

export default function DeveloperDashboardPremium({ onNotify, onBack, onLogout, currentUserEmail = "", onCurrentUserUpdated }) {
  const [users, setUsers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [trialAccounts, setTrialAccounts] = useState([]);
  const [trialAccountsError, setTrialAccountsError] = useState("");
  const [adminUserTab, setAdminUserTab] = useState("active");
  const [clientManagerSummary, setClientManagerSummary] = useState([]);
  const [trialOrg, setTrialOrg] = useState({
    trial_org_id: null,
    trial_org_name: null,
    updated_by_email: null,
    updated_at: null,
  });
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [selectedTrialOrgId, setSelectedTrialOrgId] = useState("");
  const [isSavingTrialOrg, setIsSavingTrialOrg] = useState(false);
  const [isTrialOrgConfirmOpen, setIsTrialOrgConfirmOpen] = useState(false);
  const [trialOrgConfirmText, setTrialOrgConfirmText] = useState("");
  const [pendingTrialOrgId, setPendingTrialOrgId] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [selectedAccess, setSelectedAccess] = useState([]);

  const [expandedManagerEmail, setExpandedManagerEmail] = useState("");
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userModalMode, setUserModalMode] = useState("create");
  const [userDraft, setUserDraft] = useState(EMPTY_USER_FORM);
  const [userOrgSearch, setUserOrgSearch] = useState("");
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [isOrgAccessModalOpen, setIsOrgAccessModalOpen] = useState(false);
  
  // Organization Management State
  const [orgSubTab, setOrgSubTab] = useState("pending");
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [selectedOrgDomainKey, setSelectedOrgDomainKey] = useState("");
  const [selectedTrialOrgDomainKey, setSelectedTrialOrgDomainKey] = useState("");
  const [orgDirectorySearch, setOrgDirectorySearch] = useState("");
  const [orgMembers, setOrgMembers] = useState([]);
  const [isLoadingOrgMembers, setIsLoadingOrgMembers] = useState(false);
  const [orgMembersError, setOrgMembersError] = useState("");
  const [isDeletingOrg, setIsDeletingOrg] = useState(false);
  const [deleteOrgTarget, setDeleteOrgTarget] = useState(null);
  const [deleteOrgConfirmText, setDeleteOrgConfirmText] = useState("");
  const [isOrgMemberPickerOpen, setIsOrgMemberPickerOpen] = useState(false);
  const [orgMemberPickerQuery, setOrgMemberPickerQuery] = useState("");
  
  // User Role Filter State
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  
  const userListRef = useRef(null);
  const panelRef = useRef(null);
  const previousSelectedOrgIdRef = useRef("");
  const previousSelectedTrialOrgIdRef = useRef("");

  const normalizedCurrentUserEmail = useMemo(
    () => String(currentUserEmail || "").trim().toLowerCase(),
    [currentUserEmail],
  );

  const identityLabel = useMemo(() => {
    const email = String(currentUserEmail || "").trim();
    if (!email) return "Profile";
    const name = email.split("@")[0];
    return name;
  }, [currentUserEmail]);
  const profileInitial = String(identityLabel || "Profile").trim().charAt(0).toUpperCase() || "P";

  const [navOpen, setNavOpen] = useState(false);
  const navRef = useRef(null);

  const visibleTrialAccounts = useMemo(
    () => trialAccounts.filter((trial) => trial && trial.is_explore_user !== false),
    [trialAccounts],
  );

  useEffect(() => {
    function onOutside(e) {
      if (navRef.current && !navRef.current.contains(e.target)) setNavOpen(false);
    }
    if (navOpen) document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [navOpen]);

  const selectedUser = useMemo(
    () => users.find((item) => item.email === selectedEmail) || null,
    [users, selectedEmail],
  );

  const selectedUserRole = String(selectedUser?.role || "").trim().toLowerCase();
  const selectedUserIsAdmin = selectedUserRole === "admin";

  const selectedApprovedAccess = useMemo(
    () => selectedAccess.filter((item) => String(item.access_status || "").toLowerCase() === "approved"),
    [selectedAccess],
  );

  const selectedOrg = useMemo(
    () => allOrganizations.find((org) => String(org.org_id) === String(selectedOrgId)) || null,
    [allOrganizations, selectedOrgId],
  );
  const selectedOrgCustomerCount = Number(selectedOrg?.customer_count || 0);
  const selectedOrgRevenue = Number(selectedOrg?.total_revenue || 0);
  const organizationDomainGroups = useMemo(() => buildOrganizationDomainGroups(allOrganizations), [allOrganizations]);
  const selectedOrgDomainExists = organizationDomainGroups.some((group) => group.key === selectedOrgDomainKey);
  const selectedTrialOrgDomainExists = organizationDomainGroups.some((group) => group.key === selectedTrialOrgDomainKey);
  const activeOrgDomainKey =
    (selectedOrgDomainExists ? selectedOrgDomainKey : "") ||
    findOrganizationDomainKey(allOrganizations, selectedOrgId) ||
    organizationDomainGroups[0]?.key ||
    "";
  const activeOrgDomain =
    organizationDomainGroups.find((group) => group.key === activeOrgDomainKey) ||
    organizationDomainGroups[0] ||
    null;

  const filteredAdminOrganizations = useMemo(() => {
    return (activeOrgDomain?.organizations || []).filter((org) => organizationMatchesSearch(org, orgDirectorySearch));
  }, [activeOrgDomain, orgDirectorySearch]);

  const orgMemberAccessMap = useMemo(() => {
    const map = new Map();
    orgMembers.forEach((member) => {
      map.set(String(member.email || "").toLowerCase(), member);
    });
    return map;
  }, [orgMembers]);

  const orgMembersVisible = useMemo(
    () => orgMembers.filter((member) => String(member.role || "").trim().toLowerCase() !== "admin"),
    [orgMembers],
  );

  const orgMemberRoleCounts = useMemo(() => {
    return orgMembersVisible.reduce(
      (counts, member) => {
        const role = String(member.role || "").trim().toLowerCase();
        const isClientManager = Boolean(member.is_client_manager) || Boolean(member.can_manage_employees) || role === "client_manager" || role === "manager";
        if (isClientManager) {
          counts.clientManagers += 1;
        } else {
          counts.employees += 1;
        }
        return counts;
      },
      { clientManagers: 0, employees: 0 },
    );
  }, [orgMembersVisible]);

  const orgMemberPickerUsers = useMemo(() => {
    const query = String(orgMemberPickerQuery || "").trim().toLowerCase();
    const source = users
      .slice()
      .sort((left, right) => {
        const leftName = String(left.full_name || left.email || "").toLowerCase();
        const rightName = String(right.full_name || right.email || "").toLowerCase();
        return leftName.localeCompare(rightName);
      });

    return source.filter((user) => {
      if (!query) {
        return true;
      }
      const haystack = [user.email, user.full_name, user.role].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(query);
    });
  }, [orgMemberPickerQuery, users]);

  const orgMemberPickerVisibleUsers = useMemo(
    () => orgMemberPickerUsers.filter((user) => String(user.role || "").trim().toLowerCase() !== "admin"),
    [orgMemberPickerUsers],
  );

  const sharedTrialOrgId = trialOrg.trial_org_id ? String(trialOrg.trial_org_id) : "";
  const selectedOrgIdsWithAccess = useMemo(
    () => new Set(selectedApprovedAccess.map((item) => String(item.org_id))),
    [selectedApprovedAccess],
  );

  const availableOrgOptions = useMemo(
    () => allOrganizations.filter((org) => {
      const orgId = String(org.org_id);
      if (sharedTrialOrgId && orgId === sharedTrialOrgId) {
        return false;
      }
      return !selectedOrgIdsWithAccess.has(orgId);
    }),
    [allOrganizations, selectedOrgIdsWithAccess, sharedTrialOrgId],
  );

  const [orgAccessAddId, setOrgAccessAddId] = useState("");
  const [orgAccessRemoveId, setOrgAccessRemoveId] = useState("");

  const managerSummaryByEmail = useMemo(() => {
    const map = new Map();
    clientManagerSummary.forEach((manager) => {
      map.set(String(manager.manager_email || "").toLowerCase(), manager);
    });
    return map;
  }, [clientManagerSummary]);

  const managerEmails = useMemo(
    () => users.filter((item) => item.can_manage_employees).map((item) => item.email).sort(),
    [users],
  );

  const userDraftRole = String(userDraft.role || "").toLowerCase();
  const selectedDraftManager = useMemo(
    () => managerSummaryByEmail.get(String(userDraft.manager_email || "").trim().toLowerCase()) || null,
    [managerSummaryByEmail, userDraft.manager_email],
  );

  const userOrgOptions = useMemo(() => {
    if (userDraftRole === "admin") {
      return [];
    }

    const query = String(userOrgSearch || "").trim().toLowerCase();
    const managerOrgIds = new Set((selectedDraftManager?.assigned_orgs || []).map((orgId) => String(orgId)));
    const source = userDraftRole === "employee"
      ? allOrganizations.filter((org) => managerOrgIds.has(String(org.org_id)))
      : allOrganizations;

    return source.filter((org) => {
      if (!query) {
        return true;
      }
      return organizationMatchesSearch(org, query);
    });
  }, [allOrganizations, selectedDraftManager, userDraftRole, userOrgSearch]);

  const activeUsersCount = useMemo(
    () => users.filter((item) => item.is_active).length,
    [users],
  );

  const clientManagersCount = useMemo(
    () => clientManagerSummary.length,
    [clientManagerSummary],
  );

  const employeeAccountsCount = useMemo(
    () => clientManagerSummary.reduce((total, manager) => total + (manager.employee_count || 0), 0),
    [clientManagerSummary],
  );

  const selectedManagerSummary = useMemo(() => {
    if (!expandedManagerEmail) {
      return null;
    }
    return managerSummaryByEmail.get(String(expandedManagerEmail || "").toLowerCase()) || null;
  }, [expandedManagerEmail, managerSummaryByEmail]);

  const pendingCount = pendingRequests.length;
  const visibleTrialCount = visibleTrialAccounts.length;
  const trialCount = visibleTrialCount;
  const activeTrialCount = useMemo(
    () => visibleTrialAccounts.filter((trial) => trial?.is_active !== false).length,
    [visibleTrialAccounts],
  );
  const selectedTrialOrg = useMemo(() => {
    if (!selectedTrialOrgId) {
      return null;
    }
    return allOrganizations.find((org) => String(org.org_id) === String(selectedTrialOrgId)) || null;
  }, [allOrganizations, selectedTrialOrgId]);
  const activeTrialOrgDomainKey =
    (selectedTrialOrgDomainExists ? selectedTrialOrgDomainKey : "") ||
    findOrganizationDomainKey(allOrganizations, selectedTrialOrgId) ||
    organizationDomainGroups[0]?.key ||
    "";
  const activeTrialOrgDomain =
    organizationDomainGroups.find((group) => group.key === activeTrialOrgDomainKey) ||
    organizationDomainGroups[0] ||
    null;
  const trialOrgOptions = activeTrialOrgDomain?.organizations || [];

  const currentTrialOrgName = trialOrg.trial_org_name || trialOrg.trial_org_id || "not set";
  const selectedTrialOrgName = selectedTrialOrg?.org_name || selectedTrialOrg?.org_id || "";

  useEffect(() => {
    const previousSelectedOrgId = previousSelectedOrgIdRef.current;
    previousSelectedOrgIdRef.current = String(selectedOrgId || "");

    const nextDomainKey = findOrganizationDomainKey(allOrganizations, selectedOrgId);
    if (String(previousSelectedOrgId || "") !== String(selectedOrgId || "") && nextDomainKey) {
      setSelectedOrgDomainKey(nextDomainKey);
    } else if (selectedOrgDomainKey && !selectedOrgDomainExists) {
      setSelectedOrgDomainKey(organizationDomainGroups[0]?.key || "");
    } else if (!selectedOrgDomainKey && organizationDomainGroups[0]?.key) {
      setSelectedOrgDomainKey(organizationDomainGroups[0].key);
    }
  }, [allOrganizations, organizationDomainGroups, selectedOrgDomainExists, selectedOrgDomainKey, selectedOrgId]);

  useEffect(() => {
    const previousSelectedTrialOrgId = previousSelectedTrialOrgIdRef.current;
    previousSelectedTrialOrgIdRef.current = String(selectedTrialOrgId || "");

    const nextDomainKey = findOrganizationDomainKey(allOrganizations, selectedTrialOrgId);
    if (String(previousSelectedTrialOrgId || "") !== String(selectedTrialOrgId || "") && nextDomainKey) {
      setSelectedTrialOrgDomainKey(nextDomainKey);
    } else if (selectedTrialOrgDomainKey && !selectedTrialOrgDomainExists) {
      setSelectedTrialOrgDomainKey(organizationDomainGroups[0]?.key || "");
    } else if (!selectedTrialOrgDomainKey && organizationDomainGroups[0]?.key) {
      setSelectedTrialOrgDomainKey(organizationDomainGroups[0].key);
    }
  }, [allOrganizations, organizationDomainGroups, selectedTrialOrgDomainExists, selectedTrialOrgDomainKey, selectedTrialOrgId]);

  useEffect(() => {
    if (!userListRef.current) {
      return;
    }
    userListRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [adminUserTab]);

  useEffect(() => {
    if (!panelRef.current) {
      return;
    }
    panelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [adminUserTab]);

  const syncCurrentUserIfNeeded = useCallback(async (email) => {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    if (!normalizedEmail || normalizedEmail !== normalizedCurrentUserEmail) {
      return;
    }
    await onCurrentUserUpdated?.();
  }, [normalizedCurrentUserEmail, onCurrentUserUpdated]);

  const loadUsers = useCallback(async () => {
    const data = await listDeveloperUsers();
    const nextUsers = data?.users || [];
    setUsers(nextUsers);
    setSelectedEmail((current) => {
      if (current && nextUsers.some((item) => item.email === current)) {
        return current;
      }
      return nextUsers[0]?.email || "";
    });
  }, []);

  const loadPendingRequests = useCallback(async () => {
    const data = await listPendingOrgRequests();
    setPendingRequests(data?.requests || []);
  }, []);

  const loadTrialAccounts = useCallback(async () => {
    try {
      const data = await listTrialAccounts();
      setTrialAccounts(data?.trials || []);
      setTrialAccountsError("");
    } catch (error) {
      setTrialAccounts([]);
      setTrialAccountsError(getApiErrorMessage(error, "Request failed"));
      throw error;
    }
  }, []);

  const loadClientManagerSummary = useCallback(async () => {
    const data = await listClientManagerSummary();
    setClientManagerSummary(data?.managers || []);
  }, []);

  const loadSelectedAccess = useCallback(async (email) => {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    if (!normalizedEmail) {
      setSelectedAccess([]);
      return;
    }
    const data = await getDeveloperUserOrgAccess(normalizedEmail);
    setSelectedAccess(data?.access || []);
  }, []);

  const loadTrialOrg = useCallback(async () => {
    const data = await getDeveloperTrialOrg();
    setTrialOrg({
      trial_org_id: data?.trial_org_id || null,
      trial_org_name: data?.trial_org_name || null,
      updated_by_email: data?.updated_by_email || null,
      updated_at: data?.updated_at || null,
    });
    setSelectedTrialOrgId(data?.trial_org_id || "");
  }, []);

  const loadOrganizations = useCallback(async () => {
    const data = await listOrganizations();
    const nextOrganizations = (Array.isArray(data) ? data : []).map((org) => ({
      org_id: org.org_id,
      org_name: org.name || org.org_name || org.org_id,
      domain_hint: String(org.domain_hint || org.domainHint || "").trim(),
      customer_count: Number(org.customer_count || 0),
      total_revenue: Number(org.total_revenue || 0),
    }));
    setAllOrganizations(nextOrganizations.sort((a, b) => (a.org_name || "").localeCompare(b.org_name || "")));
  }, []);

  const loadOrgMembers = useCallback(async (orgId) => {
    if (!orgId) {
      setOrgMembers([]);
      setOrgMembersError("");
      return;
    }
    setIsLoadingOrgMembers(true);
    setOrgMembersError("");
    try {
      const data = await getOrgMembers(orgId);
      setOrgMembers(Array.isArray(data?.members) ? data.members : []);
    } catch (error) {
      setOrgMembersError(getApiErrorMessage(error, "Failed to load org members"));
      setOrgMembers([]);
    } finally {
      setIsLoadingOrgMembers(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        loadUsers(),
        loadPendingRequests(),
        loadClientManagerSummary(),
        loadTrialAccounts(),
        loadTrialOrg(),
        loadOrganizations(),
      ]);
      const rejected = results.find((result) => result.status === "rejected");
      if (rejected) {
        const reason = rejected.reason instanceof Error ? rejected.reason : new Error("Request failed");
          onNotify?.(`Admin dashboard refresh had partial failures: ${getApiErrorMessage(reason, "Request failed")}`, "warning");
      }
    } catch (error) {
      onNotify?.(`Admin dashboard refresh failed: ${getApiErrorMessage(error, "Request failed")}`, "error");
    }
  }, [loadClientManagerSummary, loadOrganizations, loadPendingRequests, loadTrialAccounts, loadTrialOrg, loadUsers, onNotify]);

  useEffect(() => {
    refreshAll();
    const poll = setInterval(() => {
      refreshAll();
    }, 30000);
    return () => clearInterval(poll);
  }, [refreshAll]);

  useEffect(() => {
    if (adminUserTab !== "managers" || expandedManagerEmail || !clientManagerSummary.length) {
      return;
    }
    setExpandedManagerEmail(clientManagerSummary[0].manager_email || "");
  }, [adminUserTab, clientManagerSummary, expandedManagerEmail]);

  useEffect(() => {
    loadSelectedAccess(selectedEmail).catch((error) => {
      onNotify?.(`Failed to load user access: ${getApiErrorMessage(error, "Request failed")}`, "error");
    });
  }, [loadSelectedAccess, onNotify, selectedEmail]);

  const closeUserModal = () => {
    setIsUserModalOpen(false);
    setUserDraft(EMPTY_USER_FORM);
    setUserOrgSearch("");
    setUserModalMode("create");
    setIsSubmittingUser(false);
  };

  const openCreateUserModal = () => {
    setUserModalMode("create");
    setUserDraft(EMPTY_USER_FORM);
    setUserOrgSearch("");
    setIsUserModalOpen(true);
  };

  const openEditUserModal = (user = selectedUser) => {
    if (!user) {
      onNotify?.("Select a user first", "error");
      return;
    }
    setUserModalMode("edit");
    setUserDraft({
      email: user.email || "",
      username: user.email ? String(user.email).split("@")[0] : "",
      full_name: user.full_name || "",
      role: user.role === "admin" ? "admin" : user.can_manage_employees ? "client_manager" : "employee",
      is_active: Boolean(user.is_active),
      can_create_org_without_approval: Boolean(user.can_create_org_without_approval),
      can_manage_employees: Boolean(user.can_manage_employees),
      manager_email: user.manager_email || "",
      assigned_orgs: user.assigned_orgs || [],
    });
    setUserOrgSearch("");
    setIsUserModalOpen(true);
  };

  const submitUserForm = async (event) => {
    event.preventDefault();
    if (!userDraft.email || !String(userDraft.email).includes("@")) {
      onNotify?.("Valid email is required.", "error");
      return;
    }

    // map UI roles to API role values (API accepts 'admin' or 'user')
    const apiRole = String(userDraft.role || "").toLowerCase() === "admin" ? "admin" : "user";

    const draftRole = String(userDraft.role || "").toLowerCase();
    const selectedAssignedOrgs = Array.isArray(userDraft.assigned_orgs) ? userDraft.assigned_orgs : [];

    if (draftRole === "employee" && !String(userDraft.manager_email || "").trim()) {
      onNotify?.("Select a client manager before creating an employee.", "error");
      return;
    }

    const payload = {
      username: userDraft.username.trim() || null,
      full_name: userDraft.full_name.trim() || null,
      role: apiRole,
      is_active: true, // default active on creation
      can_create_org_without_approval: false,
      can_manage_employees: draftRole === "client_manager",
      manager_email: draftRole === "employee" && userDraft.manager_email ? userDraft.manager_email.trim() : null,
      assigned_orgs: draftRole === "admin" ? [] : selectedAssignedOrgs,
    };

    setIsSubmittingUser(true);
    try {
      if (userModalMode === "create") {
        await createDeveloperUser({
          ...payload,
          email: userDraft.email.trim().toLowerCase(),
          auth_provider: "oauth",
        });
        onNotify?.("User created.", "success");
      } else {
        await updateDeveloperUser(userDraft.email.trim().toLowerCase(), payload);
        onNotify?.("User updated.", "success");
        await syncCurrentUserIfNeeded(userDraft.email);
      }
      closeUserModal();
      await refreshAll();
    } catch (error) {
      onNotify?.(`Failed to save user: ${getApiErrorMessage(error, "Request failed")}`, "error");
    } finally {
      setIsSubmittingUser(false);
    }
  };

  const askDeleteUser = (user) => {
    setDeleteTarget(user);
    setDeleteConfirmText("");
  };

  const confirmDeleteUser = async () => {
    if (!deleteTarget) {
      return;
    }
    const expected = String(deleteTarget.email || "").trim().toLowerCase();
    if (String(deleteConfirmText || "").trim().toLowerCase() !== expected) {
      onNotify?.("Type the exact email to confirm deletion.", "error");
      return;
    }
    setIsDeletingUser(true);
    try {
      await deleteDeveloperUser(deleteTarget.email);
      onNotify?.("User removed.", "success");
      await syncCurrentUserIfNeeded(deleteTarget.email);
      setDeleteTarget(null);
      setDeleteConfirmText("");
      await refreshAll();
    } catch (error) {
      onNotify?.(`Failed to delete user: ${getApiErrorMessage(error, "Request failed")}`, "error");
    } finally {
      setIsDeletingUser(false);
    }
  };

  const openOrgAccessModal = async (user) => {
    if (!user) {
      return;
    }
    setSelectedEmail(user.email);
    setOrgAccessAddId("");
    setOrgAccessRemoveId("");
    setIsOrgAccessModalOpen(true);
  };

  const onSetOrgAccess = async (orgId, accessStatus) => {
    if (!selectedEmail) {
      return;
    }
    try {
      await setDeveloperUserOrgAccess(selectedEmail, orgId, accessStatus);
      onNotify?.("Organization access updated.", "success");
      await loadSelectedAccess(selectedEmail);
      await loadPendingRequests();
    } catch (error) {
      onNotify?.(`Failed to update organization access: ${getApiErrorMessage(error, "Request failed")}`, "error");
    }
  };

  const onAddOrgAccess = async () => {
    if (!orgAccessAddId) {
      return;
    }
    await onSetOrgAccess(orgAccessAddId, "approved");
    setOrgAccessAddId("");
  };

  const openOrgMemberPicker = async (orgId) => {
    const nextOrgId = String(orgId || "").trim();
    if (!nextOrgId) {
      return;
    }
    setSelectedOrgId(nextOrgId);
    setOrgMemberPickerQuery("");
    setIsOrgMemberPickerOpen(true);
    if (!orgMemberAccessMap.size || String(selectedOrgId) !== nextOrgId) {
      await loadOrgMembers(nextOrgId);
    }
  };

  const closeOrgMemberPicker = () => {
    setIsOrgMemberPickerOpen(false);
    setOrgMemberPickerQuery("");
  };

  const addExistingUserToOrg = async (email) => {
    const targetEmail = String(email || "").trim().toLowerCase();
    if (!targetEmail || !selectedOrgId) {
      return;
    }
    try {
      await setDeveloperUserOrgAccess(targetEmail, selectedOrgId, "approved");
      onNotify?.("User added to organization access.", "success");
      await loadOrgMembers(selectedOrgId);
      await loadUsers();
      closeOrgMemberPicker();
    } catch (error) {
      onNotify?.(`Failed to add user to organization: ${getApiErrorMessage(error, "Request failed")}`, "error");
    }
  };

  const askDeleteSelectedOrganization = () => {
    if (!selectedOrgId) {
      onNotify?.("Select an organization to delete.", "error");
      return;
    }

    setDeleteOrgTarget(selectedOrg || { org_id: selectedOrgId, org_name: selectedOrgId });
    setDeleteOrgConfirmText("");
  };

  const confirmDeleteSelectedOrganization = async () => {
    if (!deleteOrgTarget?.org_id) {
      return;
    }

    const expected = String(deleteOrgTarget.org_name || deleteOrgTarget.org_id || "").trim().toLowerCase();
    const hardDeleteExpected = `hard delete ${expected}`;
    const normalizedConfirmText = String(deleteOrgConfirmText || "").trim().toLowerCase();
    const isHardDelete = normalizedConfirmText === hardDeleteExpected;

    if (normalizedConfirmText !== expected && !isHardDelete) {
      onNotify?.("Type the exact organization name to confirm deletion.", "error");
      return;
    }

    setIsDeletingOrg(true);
    try {
      const result = await deleteOrganization(deleteOrgTarget.org_id, { hardDelete: isHardDelete });
      onNotify?.(
        result?.hard_deleted ? "Organization permanently deleted with all related data." : "Organization soft deleted.",
        "success",
      );
      if (String(selectedOrgId) === String(deleteOrgTarget.org_id)) {
        setSelectedOrgId("");
        setOrgMembers([]);
        setOrgMembersError("");
      }
      setDeleteOrgTarget(null);
      setDeleteOrgConfirmText("");
      await refreshAll();
    } catch (error) {
      onNotify?.(`Failed to delete organization: ${getApiErrorMessage(error, "Request failed")}`, "error");
    } finally {
      setIsDeletingOrg(false);
    }
  };

  const onRemoveOrgAccess = async () => {
    if (!orgAccessRemoveId) {
      return;
    }
    await onSetOrgAccess(orgAccessRemoveId, "blocked");
    setOrgAccessRemoveId("");
  };

  const onReviewRequest = async (requestId, decision) => {
    try {
      await decideOrgRequest(requestId, decision);
      onNotify?.(`Request ${decision}.`, "success");
      await refreshAll();
      if (selectedEmail) {
        await loadSelectedAccess(selectedEmail);
      }
    } catch (error) {
      onNotify?.(`Failed to review request: ${getApiErrorMessage(error, "Request failed")}`, "error");
    }
  };

  const onDeleteTrialAccount = async (trialItem) => {
    if (!trialItem || !trialItem.email) return;
    const confirmed = window.confirm(`Permanently remove explore account ${trialItem.email}? This will delete the user and related trial records.`);
    if (!confirmed) return;
    try {
      await deleteTrialAccount(trialItem.email);
      onNotify?.("Explore account removed.", "success");
      await refreshAll();
    } catch (error) {
      onNotify?.(`Failed to remove explore account: ${getApiErrorMessage(error, "Request failed")}`, "error");
    }
  };

  const onSetTrialOrg = async () => {
    if (!selectedTrialOrgId) {
      onNotify?.("Please select an organization", "error");
      return;
    }

    setPendingTrialOrgId(selectedTrialOrgId);
    setTrialOrgConfirmText("");
    setIsTrialOrgConfirmOpen(true);
  };

  const confirmTrialOrgChange = async () => {
    const expectedName = String(selectedTrialOrgName || "").trim().toLowerCase();
    const typedName = String(trialOrgConfirmText || "").trim().toLowerCase();

    if (!pendingTrialOrgId) {
      onNotify?.("Select an organization first.", "error");
      return;
    }

    if (!expectedName || typedName !== expectedName) {
      onNotify?.("Type the exact target organization name to confirm.", "error");
      return;
    }

    setIsSavingTrialOrg(true);
    try {
      const updated = await setDeveloperTrialOrg(pendingTrialOrgId);
      setTrialOrg({
        trial_org_id: updated?.trial_org_id || null,
        trial_org_name: updated?.trial_org_name || null,
        updated_by_email: updated?.updated_by_email || null,
        updated_at: updated?.updated_at || null,
      });
      onNotify?.("Trial organization updated after confirmation.", "success");
      setIsTrialOrgConfirmOpen(false);
      setPendingTrialOrgId("");
      setTrialOrgConfirmText("");
    } catch (error) {
      onNotify?.(`Failed to update trial organization: ${getApiErrorMessage(error, "Request failed")}`, "error");
    } finally {
      setIsSavingTrialOrg(false);
    }
  };

  const renderUserRow = (userItem) => {
    const userLabel = userItem.full_name || userItem.email || "?";
    const userInitial = String(userLabel).trim().charAt(0).toUpperCase() || "?";
    const isAdminUser = String(userItem.role || "").toLowerCase() === "admin";
    const isManagerUser =
      Boolean(userItem.can_manage_employees) ||
      String(userItem.role || "").toLowerCase() === "client_manager" ||
      String(userItem.role || "").toLowerCase() === "manager";
    const userAvatarRoleClass = isAdminUser ? "admin" : isManagerUser ? "manager" : "employee";
    const userAvatarRoleLabel = isAdminUser ? "Admin" : isManagerUser ? "Manager" : "Employee";
    const userAvatarRoleMark = isAdminUser ? "A" : isManagerUser ? "M" : "E";

    return (
      <article
        key={userItem.email}
        className={`dev-user-card ${selectedEmail === userItem.email ? "selected" : ""}`}
      >
        <span className={`dev-user-avatar ${userAvatarRoleClass}`} aria-label={`${userAvatarRoleLabel} user`}>
          <span className="dev-user-avatar-initial">{userInitial}</span>
          <span className="dev-user-avatar-role-mark">{userAvatarRoleMark}</span>
        </span>
        <button type="button" className="dev-user-main" onClick={() => setSelectedEmail(userItem.email)}>
          <div className="dev-user-name-row">
            <strong>{userLabel}</strong>
            <span className={`dev-pill ${userItem.is_active ? "accent" : "danger"}`}>
              {userItem.is_active ? "active" : "blocked"}
            </span>
          </div>
          <p className="dev-note">{userItem.email}</p>
          <div className="dev-user-meta-row">
            {(() => {
              const isAdmin = String(userItem.role || "").toLowerCase() === "admin";
              const isManager = Boolean(userItem.can_manage_employees) || String(userItem.role || "").toLowerCase() === "client_manager" || String(userItem.role || "").toLowerCase() === "manager";
              const roleLabel = isAdmin ? "admin" : isManager ? "client manager" : "employee";
              return <span className="dev-pill">{roleLabel}</span>;
            })()}
            {userItem.manager_email && String(userItem.manager_email || "").trim() ? <span className="dev-pill muted">manager: {userItem.manager_email}</span> : null}
          </div>
        </button>

        <div className="dev-user-actions">
          {isAdminUser ? (
            <span className="dev-pill accent dev-global-access-pill">Global access</span>
          ) : (
            <button type="button" className="dev-btn" onClick={() => openOrgAccessModal(userItem)}>
              View organization access
            </button>
          )}
          <button type="button" className="dev-btn" onClick={() => openEditUserModal(userItem)}>
            Update
          </button>
          <button type="button" className="dev-btn danger" onClick={() => askDeleteUser(userItem)}>
            Remove
          </button>
        </div>
      </article>
    );
  };

  const renderTrialRow = (trialItem) => (
    <article key={`${trialItem.org_id}:${trialItem.email}`} className="dev-trial-card">
      <div className="dev-trial-identity">
        <span className="dev-trial-avatar" aria-hidden="true">
          {(trialItem.full_name || trialItem.email || "?").trim().charAt(0).toUpperCase()}
        </span>
        <div className="dev-trial-copy">
          <div className="dev-trial-name-row">
            <strong>{trialItem.full_name || trialItem.email}</strong>
            <span className={`dev-pill ${trialItem.is_active === false ? "danger" : "accent"}`}>
              {trialItem.is_active === false ? "blocked" : "active"}
            </span>
          </div>
          <p>{trialItem.email}</p>
          <div className="dev-trial-meta">
            {trialItem.mobile ? <span>{trialItem.mobile}</span> : null}
            <span>{trialItem.org_name || trialItem.org_id || "No organization"}</span>
          </div>
        </div>
      </div>

      <div className="dev-trial-detail-grid">
        <div className={`dev-trial-login-card${trialItem.last_signin ? " has-login" : ""}`}>
          <span>Last login</span>
          <strong>{trialItem.last_signin ? formatDateTime(trialItem.last_signin) : "Never"}</strong>
          {trialItem.last_signin ? (
            <small>{formatRelativeDateTime(trialItem.last_signin)}</small>
          ) : (
            <small>No successful explore sign-in yet</small>
          )}
        </div>
      </div>

      <div className="dev-trial-actions">
        <button type="button" className="dev-btn danger" onClick={() => onDeleteTrialAccount(trialItem)}>
          Remove
        </button>
      </div>
    </article>
  );

  const renderPendingRequest = (request) => (
    <article key={request.request_id} className="dev-request-card">
      <div>
        <div className="dev-request-topline">
          <strong>{request.org_name || request.org_id}</strong>
          <AccessPill value={request.request_status || "pending"} />
        </div>
        <p>{request.requested_by_email}</p>
        <span className="dev-muted-mini">Requested {formatDateTime(request.created_at)}</span>
      </div>
      <div className="dev-inline-actions">
        <button type="button" className="dev-btn" onClick={() => onReviewRequest(request.request_id, "approved")}>Approve</button>
        <button type="button" className="dev-btn danger" onClick={() => onReviewRequest(request.request_id, "rejected")}>Reject</button>
      </div>
    </article>
  );

  const renderClientManagerCard = (manager) => {
    const expanded = expandedManagerEmail === manager.manager_email;
    return (
      <article key={manager.manager_email} className={`dev-manager-card ${expanded ? "expanded active" : ""}`}>
        <button
          type="button"
          className="dev-manager-head"
          onClick={() => setExpandedManagerEmail(manager.manager_email)}
        >
          <span className="dev-manager-avatar" aria-hidden="true">
            {(manager.manager_name || manager.manager_email || "?").trim().charAt(0).toUpperCase()}
          </span>
          <div>
            <strong>{manager.manager_name || manager.manager_email}</strong>
            <p>{manager.manager_email}</p>
          </div>
        </button>
      </article>
    );
  };

  const renderAdminUserToggles = () => (
    <>
      <button
        type="button"
        className={`dev-user-toggle ${adminUserTab === "active" ? "active" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const scrollPos = window.scrollY;
          setAdminUserTab("active");
          setTimeout(() => window.scrollTo(0, scrollPos), 0);
        }}
      >
        <span className="dev-toggle-copy">
          <span className="dev-toggle-icon dev-toggle-icon-all" aria-hidden="true">
            <svg viewBox="0 0 20 20">
              <path d="M7.2 8.6a2.7 2.7 0 1 1 0-5.4 2.7 2.7 0 0 1 0 5.4z" />
              <path d="M13.8 8.6a2.4 2.4 0 1 1 0-4.8 2.4 2.4 0 0 1 0 4.8z" />
              <path d="M2.5 16.6c0-3 1.9-5 4.7-5s4.7 2 4.7 5" />
              <path d="M11.9 12.1c.6-.3 1.2-.5 1.9-.5 2.2 0 3.7 1.8 3.7 4.6" />
            </svg>
          </span>
          <span>User</span>
        </span>
        <strong>{users.length}</strong>
      </button>
      <button
        type="button"
        className={`dev-user-toggle ${adminUserTab === "trial" ? "active" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const scrollPos = window.scrollY;
          setAdminUserTab("trial");
          setTimeout(() => window.scrollTo(0, scrollPos), 0);
        }}
      >
        <span className="dev-toggle-copy">
          <span className="dev-toggle-icon dev-toggle-icon-employee" aria-hidden="true">
            <svg viewBox="0 0 20 20">
              <path d="M5 4.2h10a1.4 1.4 0 0 1 1.4 1.4v8.8a1.4 1.4 0 0 1-1.4 1.4H5a1.4 1.4 0 0 1-1.4-1.4V5.6A1.4 1.4 0 0 1 5 4.2z" />
              <path d="M7.1 8h5.8" />
              <path d="M7.1 11h3.5" />
              <path d="M13.8 12.8l1.2 1.2 2.1-2.5" />
            </svg>
          </span>
          <span>Explore Users</span>
        </span>
        <strong>{trialCount}</strong>
      </button>
      <button
        type="button"
        className={`dev-user-toggle ${adminUserTab === "orgs" ? "active" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const scrollPos = window.scrollY;
          setAdminUserTab("orgs");
          setSelectedOrgId("");
          setOrgMembers([]);
          setTimeout(() => window.scrollTo(0, scrollPos), 0);
        }}
      >
        <span className="dev-toggle-copy">
          <span className="dev-toggle-icon dev-toggle-icon-org" aria-hidden="true">
            <svg viewBox="0 0 20 20">
              <path d="M4 16.5V5.2l6-2.4 6 2.4v11.3" />
              <path d="M2.8 16.5h14.4" />
              <path d="M7 7h.1" />
              <path d="M10 7h.1" />
              <path d="M13 7h.1" />
              <path d="M7 10h.1" />
              <path d="M10 10h.1" />
              <path d="M13 10h.1" />
              <path d="M8.7 16.5v-3.2h2.6v3.2" />
            </svg>
          </span>
          <span>Organizations</span>
        </span>
        <strong>{allOrganizations.length}</strong>
        {pendingCount ? <span className="dev-tab-dot live" aria-hidden="true" /> : null}
      </button>
      <button
        type="button"
        className={`dev-user-toggle ${adminUserTab === "managers" ? "active" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const scrollPos = window.scrollY;
          setAdminUserTab("managers");
          setTimeout(() => window.scrollTo(0, scrollPos), 0);
        }}
      >
        <span className="dev-toggle-copy">
          <span className="dev-toggle-icon dev-toggle-icon-manager" aria-hidden="true">
            <svg viewBox="0 0 20 20">
              <path d="M10 8.2a2.7 2.7 0 1 1 0-5.4 2.7 2.7 0 0 1 0 5.4z" />
              <path d="M4.6 16.8c0-3 2.2-5.1 5.4-5.1s5.4 2.1 5.4 5.1" />
              <path d="M3.2 5.8h2.1" />
              <path d="M14.7 5.8h2.1" />
              <path d="M5.3 5.8l1.4 1.4" />
              <path d="M14.7 5.8l-1.4 1.4" />
            </svg>
          </span>
          <span>Client Managers</span>
        </span>
        <strong>{clientManagersCount}</strong>
      </button>
    </>
  );

  return (
    <div className="developer-shell developer-shell-premium">
      <div className="developer-hero">
        <div className="developer-hero-copy">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <RfmLogo size={44} />
            <div>
              <h2>Admin Command Center</h2>
              {/* <div className="dev-hero-subtitle">Protected admin surface</div> */}
            </div>
          </div>

          <div className="dev-user-toggle-row dev-user-toggle-row-bottom">
            {renderAdminUserToggles()}
          </div>
        </div>

        <div className="developer-hero-actions">
          <div className="dev-hero-action-row">
            <button type="button" className="dev-btn" onClick={onBack}>Back To App</button>
            <div className="header-profile-wrap" ref={navRef}>
              <button
                type="button"
                className="header-profile-btn"
                onClick={() => setNavOpen((current) => !current)}
                aria-haspopup="menu"
                aria-expanded={navOpen}
              >
                <span className="header-profile-avatar admin" aria-label="Admin profile">
                  <span className="header-profile-avatar-initial">{profileInitial}</span>
                  <span className="header-profile-avatar-role-mark">A</span>
                </span>
                <span className="header-profile-copy">
                  <strong>{identityLabel}</strong>
                  <small>Preferences</small>
                </span>
                <span className="header-profile-chevron" aria-hidden="true">▾</span>
              </button>

              {navOpen ? (
                <div className="header-profile-menu" role="menu" aria-label="Profile actions">
                  <button
                    type="button"
                    className="header-profile-item"
                    onClick={() => {
                      setNavOpen(false);
                      window.location.href = "/analysis";
                    }}
                    role="menuitem"
                  >
                    <span className="header-profile-item-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20">
                        <path d="M3.5 16.5h13" />
                        <path d="M4.5 13.5l3.3-3.4 3 2.1 4.7-6.2" />
                        <circle cx="7.8" cy="10.1" r="1" />
                        <circle cx="10.8" cy="12.2" r="1" />
                        <circle cx="15.5" cy="6" r="1" />
                      </svg>
                    </span>
                    <span>Analysis Page</span>
                  </button>

                  <button
                    type="button"
                    className="header-profile-item"
                    onClick={() => {
                      setNavOpen(false);
                      window.location.href = "/developer";
                    }}
                    role="menuitem"
                  >
                    <span className="header-profile-item-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20">
                        <path d="M3.5 4.5h13v11h-13z" />
                        <path d="M3.5 8h13" />
                        <path d="M7 11h2.5" />
                        <path d="M7 13.5h2.5" />
                        <circle cx="13.5" cy="12.3" r="1.6" />
                        <path d="M13.5 9.6v1.1" />
                        <path d="M13.5 13.9V15" />
                        <path d="M10.8 12.3h1.1" />
                        <path d="M15.1 12.3h1.1" />
                      </svg>
                    </span>
                    <span>Admin Dashboard</span>
                  </button>

                  <button
                    type="button"
                    className="header-profile-item"
                    onClick={() => {
                      setNavOpen(false);
                      window.location.href = "/onboarding";
                    }}
                    role="menuitem"
                  >
                    <span className="header-profile-item-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20">
                        <path d="M7.3 8.4a2.7 2.7 0 1 1 0-5.4 2.7 2.7 0 0 1 0 5.4z" />
                        <path d="M2.8 16.5c0-2.8 1.9-4.8 4.5-4.8 1.2 0 2.2.4 3 1.1" />
                        <path d="M13.8 5v4.8" />
                        <path d="M11.4 7.4h4.8" />
                        <path d="M11.9 15.1l1.7 1.7 3.6-4.2" />
                      </svg>
                    </span>
                    <span>Onboarding</span>
                  </button>

                  <button
                    type="button"
                    className="header-profile-item danger"
                    onClick={() => {
                      setNavOpen(false);
                      onLogout?.();
                    }}
                    role="menuitem"
                  >
                    <span className="header-profile-item-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20">
                        <path d="M8 3h-3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h3" />
                        <path d="M13 6l4 4-4 4" />
                        <path d="M17 10H8" />
                      </svg>
                    </span>
                    <span>Logout</span>
                  </button>
                </div>
              ) : null}
            </div>
          </div>
          <div className="dev-hero-mini-stats">
            <StatCard label="Users" value={activeUsersCount} meta="currently enabled accounts" />
            <StatCard label="Explore accounts" value={trialCount} meta="accounts under Explore handling" />
          </div>
        </div>

      </div>

      <div className="developer-main-grid developer-main-grid-single">
        <section className={`developer-card dev-card-users dev-card-users-${adminUserTab}`}>
          <SectionHeader
            title={adminUserTab === "active" ? "Users" : adminUserTab === "trial" ? "Explore Users" : adminUserTab === "orgs" ? "Organizations" : "Client Managers"}
            actions={(
              <div className="dev-section-actions-inline">
                {(adminUserTab === "active" || (adminUserTab === "orgs" && selectedOrgId)) && (
                  <button type="button" className="dev-btn" onClick={openCreateUserModal}>
                    <span className="dev-btn-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20">
                        <path d="M8 9.2a3.1 3.1 0 1 1 0-6.2 3.1 3.1 0 0 1 0 6.2z" />
                        <path d="M2.7 16.6c0-3 2.1-5 5.3-5 1.1 0 2.1.2 2.9.7" />
                        <path d="M14.7 10.8v5.4" />
                        <path d="M12 13.5h5.4" />
                      </svg>
                    </span>
                    Add User
                  </button>
                )}
              </div>
            )}
          />

          {adminUserTab === "trial" && trialAccountsError ? (
            <div className="dev-error-banner">
              <p>Unable to load explore users: {trialAccountsError}</p>
              <div style={{ marginTop: 8 }}>
                <button type="button" className="dev-btn" onClick={() => loadTrialAccounts().catch((e) => onNotify?.(`Retry failed: ${getApiErrorMessage(e, 'Request failed')}`, 'error'))}>Retry</button>
              </div>
            </div>
          ) : null}

          <div ref={panelRef} className={`dev-panel-switcher dev-panel-switcher-${adminUserTab} premium-scroll`}>
            <div key={adminUserTab} className="dev-panel-swap">
              {adminUserTab === "active" ? (
                <>
                  {/* User Role Filter Buttons */}
                  <div className="dev-user-toggle-row dev-user-role-tabs" style={{ marginBottom: 16 }}>
                    <button
                      type="button"
                      className={`dev-user-toggle ${userRoleFilter === "all" ? "active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setUserRoleFilter("all");
                      }}
                    >
                      <span className="dev-toggle-copy">
                        <span className="dev-toggle-icon dev-toggle-icon-all" aria-hidden="true">
                          <svg viewBox="0 0 20 20">
                            <path d="M7.2 8.6a2.7 2.7 0 1 1 0-5.4 2.7 2.7 0 0 1 0 5.4z" />
                            <path d="M13.8 8.6a2.4 2.4 0 1 1 0-4.8 2.4 2.4 0 0 1 0 4.8z" />
                            <path d="M2.5 16.6c0-3 1.9-5 4.7-5s4.7 2 4.7 5" />
                            <path d="M11.9 12.1c.6-.3 1.2-.5 1.9-.5 2.2 0 3.7 1.8 3.7 4.6" />
                          </svg>
                        </span>
                        <span>All Users</span>
                      </span>
                      <strong>{users.length}</strong>
                    </button>
                    <button
                      type="button"
                      className={`dev-user-toggle ${userRoleFilter === "admin" ? "active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setUserRoleFilter("admin");
                      }}
                    >
                      <span className="dev-toggle-copy">
                        <span className="dev-toggle-icon dev-toggle-icon-admin" aria-hidden="true">
                          <svg viewBox="0 0 20 20">
                            <path d="M10 2.8l5.7 2.2v4.2c0 3.6-2.2 6.4-5.7 8-3.5-1.6-5.7-4.4-5.7-8V5L10 2.8z" />
                            <path d="M7.5 10l1.7 1.7 3.4-3.7" />
                          </svg>
                        </span>
                        <span>Admin</span>
                      </span>
                      <strong>{users.filter(u => u.role === "admin").length}</strong>
                    </button>
                    <button
                      type="button"
                      className={`dev-user-toggle ${userRoleFilter === "manager" ? "active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setUserRoleFilter("manager");
                      }}
                    >
                      <span className="dev-toggle-copy">
                        <span className="dev-toggle-icon dev-toggle-icon-manager" aria-hidden="true">
                          <svg viewBox="0 0 20 20">
                            <path d="M10 8.2a2.7 2.7 0 1 1 0-5.4 2.7 2.7 0 0 1 0 5.4z" />
                            <path d="M4.6 16.8c0-3 2.2-5.1 5.4-5.1s5.4 2.1 5.4 5.1" />
                            <path d="M3.2 5.8h2.1" />
                            <path d="M14.7 5.8h2.1" />
                            <path d="M5.3 5.8l1.4 1.4" />
                            <path d="M14.7 5.8l-1.4 1.4" />
                          </svg>
                        </span>
                        <span>Client Manager</span>
                      </span>
                      <strong>{users.filter(u => u.can_manage_employees).length}</strong>
                    </button>
                    <button
                      type="button"
                      className={`dev-user-toggle ${userRoleFilter === "employee" ? "active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setUserRoleFilter("employee");
                      }}
                    >
                      <span className="dev-toggle-copy">
                        <span className="dev-toggle-icon dev-toggle-icon-employee" aria-hidden="true">
                          <svg viewBox="0 0 20 20">
                            <path d="M5 3.5h10a1.5 1.5 0 0 1 1.5 1.5v10A1.5 1.5 0 0 1 15 16.5H5A1.5 1.5 0 0 1 3.5 15V5A1.5 1.5 0 0 1 5 3.5z" />
                            <path d="M10 9.2a1.8 1.8 0 1 1 0-3.6 1.8 1.8 0 0 1 0 3.6z" />
                            <path d="M6.8 13.8c.4-1.6 1.6-2.5 3.2-2.5s2.8.9 3.2 2.5" />
                          </svg>
                        </span>
                        <span>Employee</span>
                      </span>
                      <strong>{users.filter(u => !u.can_manage_employees && (!u.role || u.role === "user")).length}</strong>
                    </button>
                  </div>

                  <div className="dev-user-list premium-scroll dev-user-list-swap">
                    {users.filter(u => {
                      if (userRoleFilter === "all") return true;
                      if (userRoleFilter === "admin") return u.role === "admin";
                      if (userRoleFilter === "manager") return u.can_manage_employees;
                      if (userRoleFilter === "employee") return !u.can_manage_employees && (!u.role || u.role === "user");
                      return true;
                    }).map(renderUserRow)}
                    {!users.filter(u => {
                      if (userRoleFilter === "all") return true;
                      if (userRoleFilter === "admin") return u.role === "admin";
                      if (userRoleFilter === "manager") return u.can_manage_employees;
                      if (userRoleFilter === "employee") return !u.can_manage_employees && (!u.role || u.role === "user");
                      return true;
                    }).length ? <p className="dev-empty-state">No users found.</p> : null}
                  </div>
                </>
              ) : null}

              {adminUserTab === "trial" ? (
                <div className="dev-explore-workspace">
                  <div className="dev-explore-overview">
                    <div className="dev-explore-summary">
                      <span>Explore directory</span>
                      <strong>{visibleTrialCount} users</strong>
                    </div>
                    <div className="dev-explore-metrics">
                      <div>
                        <span>Active</span>
                        <strong>{activeTrialCount}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="dev-user-list dev-trial-list-premium premium-scroll dev-user-list-swap">
                    {visibleTrialAccounts.map(renderTrialRow)}
                    {!visibleTrialAccounts.length ? (
                      <div className="dev-empty-state dev-explore-empty">
                        <strong>No explore users found.</strong>
                        <p>New Explore signups will appear here with contact and trial activity.</p>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {adminUserTab === "orgs" ? (
                <>
                  {/* Organization Sub-Tab Buttons */}
                  <div className="dev-user-toggle-row dev-org-sub-tabs" style={{ marginBottom: 16 }}>
                    <button
                      type="button"
                      className={`dev-user-toggle ${orgSubTab === "pending" ? "active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const scrollPos = window.scrollY;
                        setOrgSubTab("pending");
                        setTimeout(() => window.scrollTo(0, scrollPos), 0);
                      }}
                    >
                      <span className="dev-toggle-copy">
                        <span className="dev-toggle-icon dev-toggle-icon-pending" aria-hidden="true">
                          <svg viewBox="0 0 20 20">
                            <path d="M6 3.5h8a1.5 1.5 0 0 1 1.5 1.5v11.5h-11V5A1.5 1.5 0 0 1 6 3.5z" />
                            <path d="M7.2 7h4.2" />
                            <path d="M7.2 10h2.8" />
                            <circle cx="13.4" cy="12.9" r="2.6" />
                            <path d="M13.4 11.6v1.5l1 .7" />
                          </svg>
                        </span>
                        <span>Organization Pending Requests</span>
                      </span>
                      <strong>{pendingCount}</strong>
                    </button>

                    <button
                      type="button"
                      className={`dev-user-toggle ${orgSubTab === "access" ? "active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const scrollPos = window.scrollY;
                        setOrgSubTab("access");
                        setTimeout(() => window.scrollTo(0, scrollPos), 0);
                      }}
                    >
                      <span className="dev-toggle-copy">
                        <span className="dev-toggle-icon dev-toggle-icon-access" aria-hidden="true">
                          <svg viewBox="0 0 20 20">
                            <path d="M3.5 16.5V5.7L9 3.2l5.5 2.5v10.8" />
                            <path d="M2.5 16.5h8.2" />
                            <path d="M6.6 8h.1" />
                            <path d="M9 8h.1" />
                            <path d="M11.4 8h.1" />
                            <path d="M6.6 10.8h.1" />
                            <path d="M9 10.8h.1" />
                            <path d="M13.9 11.1a2.1 2.1 0 1 1 0 4.2 2.1 2.1 0 0 1 0-4.2z" />
                            <path d="M11.1 17c.4-1.3 1.4-2 2.8-2s2.4.7 2.8 2" />
                          </svg>
                        </span>
                        <span>Organization Access Accounts</span>
                      </span>
                      <strong>{allOrganizations.length}</strong>
                    </button>
                  </div>

                  <div className="dev-org-tab-panel">
                    <div key={orgSubTab} className="dev-panel-swap">
                      {/* Pending Requests View */}
                      {orgSubTab === "pending" ? (
                        <div className="dev-slide-card dev-slide-card-pending">
                          <div className="dev-slide-head">
                            <div>
                              <div className="dev-mini-label">Pending approval queue</div>
                              <h4>Organization Requests</h4>
                            </div>
                            <div className="dev-slide-hero-count">
                              <span>{pendingCount}</span>
                              <small>{pendingCount ? "approvals remain" : "no pending request"}</small>
                            </div>
                          </div>
                          {pendingCount ? (
                            <p className="dev-slide-message">
                              These requests are waiting for approval. Review and approve or reject from here.
                            </p>
                          ) : null}
                          <div className="dev-request-list premium-scroll compact">
                            {pendingRequests.map(renderPendingRequest)}
                            {!pendingRequests.length ? <p className="dev-empty-state">No pending organization requests.</p> : null}
                          </div>
                        </div>
                      ) : null}

                      {/* Organization Access Accounts View */}
                      {orgSubTab === "access" ? (
                        <div className="dev-slide-card dev-slide-card-org-access">
                          <div className="dev-slide-head dev-org-access-hero">
                            <div>
                              <div className="dev-mini-label">Organization management</div>
                              <h4>Organization Access Accounts</h4>
                              <p className="dev-slide-message">
                                {sharedTrialOrgId && String(selectedOrgId) === sharedTrialOrgId
                                  ? "This is the default trial organization, so every account is listed here automatically."
                                  : "Select an organization to view and manage member access."}
                              </p>
                            </div>
                            <div className="dev-slide-hero-count">
                              <span>{allOrganizations.length}</span>
                              <small>organizations</small>
                            </div>
                          </div>

                          <div className="dev-org-access-workspace">
                            <aside className="dev-org-directory">
                              <div className="dev-org-directory-head">
                                <div>
                                  <span className="dev-mini-label">Directory</span>
                                  <strong>Organizations</strong>
                                </div>
                                <span>{filteredAdminOrganizations.length}</span>
                              </div>
                              <div className="dev-org-directory-search">
                                <svg viewBox="0 0 20 20" aria-hidden="true">
                                  <circle cx="9" cy="9" r="5" />
                                  <path d="m13 13 4 4" />
                                </svg>
                                <input
                                  type="search"
                                  value={orgDirectorySearch}
                                  onChange={(event) => setOrgDirectorySearch(event.target.value)}
                                  placeholder="Search organizations"
                                  aria-label="Search organizations"
                                />
                              </div>
                              <div className="dev-domain-selector" role="tablist" aria-label="Organization sectors">
                                {organizationDomainGroups.map((group) => (
                                  <button
                                    key={group.key}
                                    type="button"
                                    className={`dev-domain-option ${group.key === activeOrgDomainKey ? "active" : ""}`.trim()}
                                    onClick={() => {
                                      setSelectedOrgDomainKey(group.key);
                                      setOrgDirectorySearch("");
                                      const firstOrgId = group.organizations[0]?.org_id || "";
                                      if (firstOrgId) {
                                        setSelectedOrgId(firstOrgId);
                                        loadOrgMembers(firstOrgId);
                                      }
                                    }}
                                    role="tab"
                                    aria-selected={group.key === activeOrgDomainKey}
                                  >
                                    <span>{group.label}</span>
                                    <strong>{group.organizations.length}</strong>
                                  </button>
                                ))}
                              </div>
                              <div className="dev-org-selector premium-scroll">
                                {filteredAdminOrganizations.map((org) => (
                                  <button
                                    key={org.org_id}
                                    type="button"
                                    className={`dev-org-item ${selectedOrgId === org.org_id ? "active" : ""}`}
                                    onClick={() => {
                                      setSelectedOrgId(org.org_id);
                                      loadOrgMembers(org.org_id);
                                    }}
                                  >
                                    <span className="dev-org-item-icon" aria-hidden="true">
                                      <svg viewBox="0 0 20 20">
                                        <path d="M4 16.5V5.2l6-2.4 6 2.4v11.3" />
                                        <path d="M2.8 16.5h14.4" />
                                        <path d="M7 7h.1" />
                                        <path d="M10 7h.1" />
                                        <path d="M13 7h.1" />
                                        <path d="M8.7 16.5v-3.2h2.6v3.2" />
                                      </svg>
                                    </span>
                                    <span className="dev-org-item-copy">
                                      <span className="dev-org-item-name">{org.org_name}</span>
                                      <span className="dev-org-item-meta">
                                        <span>{getOrganizationDomainLabel(org)}</span>
                                        <span>{formatNumber(org.customer_count || 0)} customers</span>
                                      </span>
                                    </span>
                                  </button>
                                ))}
                                {!allOrganizations.length ? <p className="dev-empty-state">No organizations found.</p> : null}
                                {allOrganizations.length && !filteredAdminOrganizations.length ? (
                                  <p className="dev-empty-state">No organizations match this search.</p>
                                ) : null}
                              </div>
                            </aside>

                            <div className="dev-org-members-panel">
                              <div className="dev-org-default-strip">
                                <div className="dev-org-default-current">
                                  <span className="dev-org-access-label">Default Explore Org</span>
                                  <strong>{currentTrialOrgName}</strong>
                                  <p>
                                    {trialOrg.updated_at
                                      ? `Updated ${formatRelativeDateTime(trialOrg.updated_at)} by ${trialOrg.updated_by_email || "admin"}`
                                      : "Explore users and demo access open this organization first."}
                                  </p>
                                </div>
                                <div className="dev-org-default-change">
                                  <select
                                    value={activeTrialOrgDomainKey}
                                    onChange={(event) => {
                                      const nextDomainKey = event.target.value;
                                      setSelectedTrialOrgDomainKey(nextDomainKey);
                                      const nextGroup = organizationDomainGroups.find((group) => group.key === nextDomainKey);
                                      setSelectedTrialOrgId(nextGroup?.organizations?.[0]?.org_id || "");
                                    }}
                                    disabled={isSavingTrialOrg}
                                    aria-label="Select default explore organization sector"
                                  >
                                    {organizationDomainGroups.map((group) => (
                                      <option key={group.key} value={group.key}>
                                        {group.label} ({group.organizations.length})
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    value={selectedTrialOrgId}
                                    onChange={(event) => {
                                      const nextOrgId = event.target.value;
                                      setSelectedTrialOrgId(nextOrgId);
                                      setSelectedTrialOrgDomainKey(findOrganizationDomainKey(allOrganizations, nextOrgId));
                                    }}
                                    disabled={isSavingTrialOrg}
                                    aria-label="Select default explore organization"
                                  >
                                    <option value="">Select organization</option>
                                    {trialOrgOptions.map((org) => (
                                      <option key={org.org_id} value={org.org_id}>
                                        {org.org_name} ({org.org_id.substring(0, 8)})
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    className="dev-btn"
                                    onClick={onSetTrialOrg}
                                    disabled={isSavingTrialOrg || !selectedTrialOrgId || String(selectedTrialOrgId) === String(trialOrg.trial_org_id || "")}
                                  >
                                    {isSavingTrialOrg ? "Saving..." : "Set Default"}
                                  </button>
                                </div>
                              </div>
                              {selectedOrgId ? (
                                <>
                              <div className="dev-org-members-header">
                                <div>
                                  <span className="dev-mini-label">Member access</span>
                                  <h5>{selectedOrg?.org_name}</h5>
                                  <p>{selectedOrg?.org_id}</p>
                                </div>
                                  <div className="dev-org-selected-insights" aria-label="Selected organization metrics">
                                    <span className="dev-org-insight primary">
                                      <strong>{formatNumber(selectedOrgCustomerCount)}</strong>
                                      <small>Customers</small>
                                    </span>
                                    <span className="dev-org-insight revenue">
                                      <strong>{formatCompactCurrency(selectedOrgRevenue)}</strong>
                                      <small>Total revenue</small>
                                    </span>
                                    <span className="dev-org-insight">
                                      <strong>{formatNumber(orgMembersVisible.length)}</strong>
                                      <small>Access accounts</small>
                                    </span>
                                    <span className="dev-org-insight">
                                      <strong>{formatNumber(orgMemberRoleCounts.clientManagers)}</strong>
                                      <small>Managers</small>
                                    </span>
                                  </div>
                                  <div className="dev-org-members-actions">
                                    <button
                                      type="button"
                                      className="dev-btn"
                                      onClick={() => openOrgMemberPicker(selectedOrgId)}
                                      disabled={sharedTrialOrgId && String(selectedOrgId) === sharedTrialOrgId}
                                    >
                                      <span className="dev-btn-icon" aria-hidden="true">
                                        <svg viewBox="0 0 20 20">
                                          <path d="M8 9.2a3.1 3.1 0 1 1 0-6.2 3.1 3.1 0 0 1 0 6.2z" />
                                          <path d="M2.7 16.6c0-3 2.1-5 5.3-5 1.1 0 2.1.2 2.9.7" />
                                          <path d="M14.7 10.8v5.4" />
                                          <path d="M12 13.5h5.4" />
                                        </svg>
                                      </span>
                                      {sharedTrialOrgId && String(selectedOrgId) === sharedTrialOrgId ? "Default Access" : "Add User"}
                                    </button>
                                    <button
                                      type="button"
                                      className="dev-btn danger"
                                      onClick={askDeleteSelectedOrganization}
                                      disabled={isDeletingOrg}
                                    >
                                      <span className="dev-btn-icon" aria-hidden="true">
                                        <svg viewBox="0 0 20 20">
                                          <path d="M4 6h12" />
                                          <path d="M7 6V4h6v2" />
                                          <path d="M6 6l1 10h6l1-10" />
                                        </svg>
                                      </span>
                                      {isDeletingOrg ? "Deleting..." : "Delete Org"}
                                    </button>
                                  </div>
                              </div>
                              {isLoadingOrgMembers ? (
                                <p className="dev-loading">Loading members...</p>
                              ) : orgMembersError ? (
                                <div className="dev-error-banner">
                                  <p>{orgMembersError}</p>
                                </div>
                              ) : (
                                <div className="dev-org-members-list premium-scroll">
                                  {orgMembersVisible.map((member) => (
                                    <div key={member.email} className="dev-org-member-item">
                                      <div className="dev-member-avatar" aria-hidden="true">
                                        {(member.full_name || member.email || "?").trim().charAt(0).toUpperCase()}
                                      </div>
                                      <div className="dev-member-info">
                                        <strong>{member.full_name || member.email}</strong>
                                        <p>{member.email}</p>
                                      </div>
                                      <span className={`dev-pill ${member.is_active ? "accent" : "danger"}`}>
                                        {member.is_active ? "Active" : "Inactive"}
                                      </span>
                                      <div className="dev-member-actions">
                                        {member.access_status === "approved" ? (
                                          <button
                                            type="button"
                                            className="dev-btn danger-outline"
                                            onClick={async () => {
                                              try {
                                                await setDeveloperUserOrgAccess(member.email, selectedOrgId, "blocked");
                                                await loadOrgMembers(selectedOrgId);
                                                onNotify?.(`${member.email} has been blocked from this organization`, "success");
                                              } catch (error) {
                                                onNotify?.(`Failed to block ${member.email}: ${getApiErrorMessage(error, "Request failed")}`, "error");
                                              }
                                            }}
                                          >
                                            Block
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            className="dev-btn"
                                            onClick={async () => {
                                              try {
                                                await setDeveloperUserOrgAccess(member.email, selectedOrgId, "approved");
                                                await loadOrgMembers(selectedOrgId);
                                                onNotify?.(`${member.email} has been allowed access to this organization`, "success");
                                              } catch (error) {
                                                onNotify?.(`Failed to allow ${member.email}: ${getApiErrorMessage(error, "Request failed")}`, "error");
                                              }
                                            }}
                                          >
                                            Allow
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                  {!orgMembersVisible.length ? (
                                    <div className="dev-empty-state dev-org-empty-state">
                                      <strong>No accounts have access to this organization yet.</strong>
                                      <p>Use Add User to assign an existing account from the current user directory.</p>
                                    </div>
                                  ) : null}
                                </div>
                              )}
                                </>
                              ) : (
                                <div className="dev-org-members-placeholder">
                                  <span className="dev-org-placeholder-icon" aria-hidden="true">
                                    <svg viewBox="0 0 20 20">
                                      <path d="M4 16.5V5.2l6-2.4 6 2.4v11.3" />
                                      <path d="M2.8 16.5h14.4" />
                                      <path d="M7 7h.1" />
                                      <path d="M10 7h.1" />
                                      <path d="M13 7h.1" />
                                    </svg>
                                  </span>
                                  <strong>Select an organization</strong>
                                  <p>Choose a record from the directory to review account access.</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </>
              ) : null}

              {adminUserTab === "managers" ? (
                <div className="dev-slide-card dev-slide-card-managers">
                  <div className="dev-slide-head dev-manager-hero">
                    <div>
                      <div className="dev-mini-label">Client manager summary</div>
                      <h4>Manager accounts and assigned employees</h4>
                      <p className="dev-slide-message">
                        Select a client manager to review their employee roster and organization assignments.
                      </p>
                    </div>
                    <div className="dev-slide-hero-count">
                      <span>{clientManagersCount}</span>
                      <small>{employeeAccountsCount} employee accounts</small>
                    </div>
                  </div>
                  <div className="dev-manager-workspace">
                    <aside className="dev-manager-directory">
                      <div className="dev-manager-directory-head">
                        <div>
                          <span className="dev-mini-label">Directory</span>
                          <strong>Client Managers</strong>
                        </div>
                        <span>{clientManagersCount}</span>
                      </div>
                      <div className="dev-manager-list premium-scroll compact">
                        {clientManagerSummary.map(renderClientManagerCard)}
                        {!clientManagerSummary.length ? <p className="dev-empty-state">No client managers found.</p> : null}
                      </div>
                    </aside>

                    <div className="dev-manager-detail-panel">
                      {selectedManagerSummary ? (
                        <>
                          <div className="dev-manager-detail-head">
                            <div>
                              <span className="dev-mini-label">Employee roster</span>
                              <h5>{selectedManagerSummary.manager_name || selectedManagerSummary.manager_email}</h5>
                              <p>{selectedManagerSummary.manager_email}</p>
                            </div>
                            <div className="dev-manager-detail-stats">
                              <span className="dev-manager-detail-stat employee">
                                <strong>{selectedManagerSummary.employee_count || 0}</strong>
                                <small>Employees</small>
                              </span>
                              <span className="dev-manager-detail-stat active">
                                <strong>{selectedManagerSummary.active_employee_count || 0}</strong>
                                <small>Active</small>
                              </span>
                            </div>
                          </div>
                          <div className="dev-manager-body premium-scroll">
                            {(selectedManagerSummary.employees || []).map((employee) => (
                              <div key={employee.email} className="dev-employee-row">
                                <div className="dev-member-avatar" aria-hidden="true">
                                  {(employee.full_name || employee.email || "?").trim().charAt(0).toUpperCase()}
                                </div>
                                <div className="dev-employee-info">
                                  <strong>{employee.full_name || employee.email}</strong>
                                  <p>{employee.email}</p>
                                </div>
                                <span className={`dev-pill ${employee.is_active ? "accent" : "danger"}`}>
                                  {employee.is_active ? "Active" : "Blocked"}
                                </span>
                                <div className="dev-org-tag-wrap">
                                  {(employee.assigned_org_names || []).length ? (
                                    employee.assigned_org_names.map((orgName) => (
                                      <span key={orgName} className="dev-org-tag">{orgName}</span>
                                    ))
                                  ) : (
                                    <span className="dev-muted-mini">No organization access</span>
                                  )}
                                </div>
                              </div>
                            ))}
                            {!selectedManagerSummary.employees?.length ? (
                              <div className="dev-empty-state dev-org-empty-state">
                                <strong>No employee accounts mapped to this manager.</strong>
                                <p>Assigned employees will appear here with their organization access.</p>
                              </div>
                            ) : null}
                          </div>
                        </>
                      ) : (
                        <div className="dev-org-members-placeholder">
                          <span className="dev-org-placeholder-icon" aria-hidden="true">
                            <svg viewBox="0 0 20 20">
                              <path d="M10 8.2a2.7 2.7 0 1 1 0-5.4 2.7 2.7 0 0 1 0 5.4z" />
                              <path d="M4.6 16.8c0-3 2.2-5.1 5.4-5.1s5.4 2.1 5.4 5.1" />
                              <path d="M3.2 5.8h2.1" />
                              <path d="M14.7 5.8h2.1" />
                            </svg>
                          </span>
                          <strong>Select a client manager</strong>
                          <p>Choose a manager from the directory to review employee assignments.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <div className="developer-side-stack">
          {/* Governance and trial organization controls removed per new admin layout. Summary cards added above. */}
        </div>
      </div>

      {isUserModalOpen ? (
        <ModalShell
          title={userModalMode === "create" ? "Add User" : "Update User"}
          subtitle="Google OAuth is now the only sign-in option, so the form focuses on profile, permissions, and team ownership."
          onClose={closeUserModal}
          wide
          footer={(
            <div className="dev-modal-footer-actions">
              <button type="button" className="dev-btn" onClick={closeUserModal}>Cancel</button>
              <button type="button" className="dev-btn primary" onClick={submitUserForm} disabled={isSubmittingUser}>
                {isSubmittingUser ? "Saving..." : userModalMode === "create" ? "Create User" : "Update User"}
              </button>
            </div>
          )}
        >
          <form className="dev-form-grid dev-user-form" onSubmit={submitUserForm}>
            <label>
              <span>Email</span>
              <input
                type="email"
                value={userDraft.email}
                onChange={(event) => setUserDraft((current) => ({ ...current, email: event.target.value }))}
                placeholder="user@example.com"
                required
                disabled={userModalMode === "edit"}
              />
            </label>
            <label>
              <span>Username</span>
              <input
                type="text"
                value={userDraft.username}
                onChange={(event) => setUserDraft((current) => ({ ...current, username: event.target.value }))}
                placeholder="Optional username"
              />
            </label>
            <label className="dev-form-span-2">
              <span>Full name</span>
              <input
                type="text"
                value={userDraft.full_name}
                onChange={(event) => setUserDraft((current) => ({ ...current, full_name: event.target.value }))}
                placeholder="User full name"
              />
            </label>
            <label>
              <span>Role</span>
              <select
                value={userDraft.role}
                onChange={(event) => {
                  const nextRole = event.target.value;
                  setUserDraft((current) => ({
                    ...current,
                    role: nextRole,
                    manager_email: nextRole === "employee" ? current.manager_email : "",
                    assigned_orgs: [],
                  }));
                  setUserOrgSearch("");
                }}
                disabled={userModalMode === "edit"}
              >
                <option value="admin">admin</option>
                <option value="client_manager">client manager</option>
                <option value="employee">employee</option>
              </select>
            </label>

            {userDraftRole === "employee" ? (
              <>
                <label>
                  <span>Manager (search)</span>
                  <input
                    list="manager-list"
                    placeholder="Type manager email"
                    value={userDraft.manager_email}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      if (userModalMode === "edit") {
                        const currentValue = String(userDraft.manager_email || "").trim().toLowerCase();
                        const nextTrimmed = String(nextValue || "").trim().toLowerCase();
                        if (currentValue && nextTrimmed && nextTrimmed !== currentValue) {
                          onNotify?.("Remove the current manager first, then assign a different client manager.", "error");
                          return;
                        }
                      }
                      setUserDraft((current) => ({ ...current, manager_email: nextValue, assigned_orgs: [] }));
                      setUserOrgSearch("");
                    }}
                  />
                  <datalist id="manager-list">
                    {managerEmails.map((email) => (
                      <option key={email} value={email} />
                    ))}
                  </datalist>
                </label>

                {userModalMode === "edit" && userDraft.manager_email && String(userDraft.manager_email).trim() ? (
                  <p className="dev-muted-mini">Clear this field to remove the current manager assignment, then save again to map the employee to a different client manager.</p>
                ) : null}

              </>
            ) : null}

            {userDraftRole === "client_manager" || userDraftRole === "employee" ? (
              <div className="dev-form-span-2 dev-user-org-assignment">
                <div className="dev-user-org-assignment-head">
                  <div>
                    <span className="dev-mini-label">
                      {userDraftRole === "client_manager" ? "Manager organization access" : "Employee organization access"}
                    </span>
                    <strong>
                      {userDraftRole === "client_manager"
                        ? "Select organizations this client manager can control"
                        : selectedDraftManager
                        ? `Select from ${selectedDraftManager.manager_email}'s organizations`
                        : "Select a manager to load organizations"}
                    </strong>
                    <p>
                      {userDraftRole === "client_manager"
                        ? "Search and assign one or more approved organizations."
                        : "Employees can only receive access to organizations already assigned to their client manager."}
                    </p>
                  </div>
                  <span className="dev-org-count-pill">{(userDraft.assigned_orgs || []).length} selected</span>
                </div>

                <label className="dev-user-org-search">
                  <span>Search organizations</span>
                  <input
                    type="text"
                    value={userOrgSearch}
                    onChange={(event) => setUserOrgSearch(event.target.value)}
                    placeholder={userDraftRole === "employee" && !selectedDraftManager ? "Select a manager first" : "Search by organization name or id"}
                    disabled={userDraftRole === "employee" && !selectedDraftManager}
                  />
                </label>

                <div className="dev-user-org-list premium-scroll">
                  {userOrgOptions.map((org) => {
                    const checked = (userDraft.assigned_orgs || []).includes(org.org_id);
                    return (
                      <button
                        key={org.org_id}
                        type="button"
                        className={`dev-user-org-option ${checked ? "selected" : ""}`}
                        onClick={() => setUserDraft((current) => {
                          const currentSet = new Set(current.assigned_orgs || []);
                          if (currentSet.has(org.org_id)) {
                            currentSet.delete(org.org_id);
                          } else {
                            currentSet.add(org.org_id);
                          }
                          return { ...current, assigned_orgs: Array.from(currentSet) };
                        })}
                      >
                        <span className="dev-user-org-check" aria-hidden="true">{checked ? "✓" : ""}</span>
                        <span className="dev-user-org-copy">
                          <strong>{org.org_name}</strong>
                          <small>{getOrganizationDomainLabel(org)} - {org.org_id}</small>
                        </span>
                      </button>
                    );
                  })}

                  {!userOrgOptions.length ? (
                    <div className="dev-user-org-empty">
                      {userDraftRole === "employee" && !selectedDraftManager
                        ? "Choose a client manager first. Only that manager's organizations will appear here."
                        : "No organizations match this search."}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </form>
        </ModalShell>
      ) : null}

      {isOrgMemberPickerOpen && selectedOrg ? (
        <ModalShell
          title={`Add existing user to ${selectedOrg.org_name || selectedOrg.org_id}`}
          subtitle="Pick an existing account from the directory and grant it access to this organization."
          onClose={closeOrgMemberPicker}
          wide
          footer={(
            <div className="dev-modal-footer-actions">
              <button type="button" className="dev-btn" onClick={closeOrgMemberPicker}>Close</button>
            </div>
          )}
        >
          <div className="dev-org-picker-shell">
            <label className="dev-org-picker-search">
              <span>Search users</span>
              <input
                type="text"
                value={orgMemberPickerQuery}
                onChange={(event) => setOrgMemberPickerQuery(event.target.value)}
                placeholder="Search by name, email, or role"
              />
            </label>

            <div className="dev-org-picker-list premium-scroll">
              {orgMemberPickerVisibleUsers.map((user) => {
                const memberRecord = orgMemberAccessMap.get(String(user.email || "").toLowerCase()) || null;
                const isAlreadyApproved = String(memberRecord?.access_status || "").toLowerCase() === "approved";
                const isBlocked = String(memberRecord?.access_status || "").toLowerCase() === "blocked";
                const isPending = String(memberRecord?.access_status || "").toLowerCase() === "pending";

                return (
                  <article key={user.email} className="dev-org-picker-row">
                    <div className="dev-org-picker-info">
                      <strong>{user.full_name || user.email}</strong>
                      <p>{user.email}</p>
                      <span className="dev-org-picker-meta">
                        {user.role === "admin" ? "admin" : user.can_manage_employees ? "client manager" : "employee"}
                        {isAlreadyApproved ? " · already added" : isBlocked ? " · blocked" : isPending ? " · pending" : ""}
                      </span>
                    </div>

                    <div className="dev-org-picker-actions">
                      {user.role === "admin" ? (
                        <span className="dev-pill accent">global access</span>
                      ) : isAlreadyApproved ? (
                        <span className="dev-pill accent">added</span>
                      ) : (
                        <button
                          type="button"
                          className="dev-btn primary"
                          onClick={() => addExistingUserToOrg(user.email)}
                        >
                            Add to organization
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}

              {!orgMemberPickerVisibleUsers.length ? (
                <p className="dev-empty-state">No users match your search.</p>
              ) : null}
            </div>
          </div>
        </ModalShell>
      ) : null}

      {deleteTarget ? (
        <ModalShell
          title="Remove Account"
          subtitle={`Type ${deleteTarget.email} to confirm removal. This prevents accidental deletion.`}
          onClose={() => {
            setDeleteTarget(null);
            setDeleteConfirmText("");
          }}
          footer={(
            <div className="dev-modal-footer-actions">
              <button
                type="button"
                className="dev-btn"
                onClick={() => {
                  setDeleteTarget(null);
                  setDeleteConfirmText("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dev-btn danger"
                onClick={confirmDeleteUser}
                disabled={isDeletingUser || String(deleteConfirmText || "").trim().toLowerCase() !== String(deleteTarget.email || "").trim().toLowerCase()}
              >
                {isDeletingUser ? "Removing..." : "Remove Account"}
              </button>
            </div>
          )}
        >
          <label className="dev-delete-confirm">
            <span>Confirm by typing the email address</span>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(event) => setDeleteConfirmText(event.target.value)}
              placeholder={deleteTarget.email}
            />
          </label>
        </ModalShell>
      ) : null}

      {deleteOrgTarget ? (
        <ModalShell
          title="Delete Organization"
          subtitle={`Type ${deleteOrgTarget.org_name || deleteOrgTarget.org_id} to confirm deletion. This protects uploaded data from accidental removal.`}
          onClose={() => {
            setDeleteOrgTarget(null);
            setDeleteOrgConfirmText("");
          }}
          footer={(
            <div className="dev-modal-footer-actions">
              <button
                type="button"
                className="dev-btn"
                onClick={() => {
                  setDeleteOrgTarget(null);
                  setDeleteOrgConfirmText("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dev-btn danger"
                onClick={confirmDeleteSelectedOrganization}
                disabled={
                  isDeletingOrg ||
                  ![
                    String(deleteOrgTarget.org_name || deleteOrgTarget.org_id || "").trim().toLowerCase(),
                    `hard delete ${String(deleteOrgTarget.org_name || deleteOrgTarget.org_id || "").trim().toLowerCase()}`,
                  ].includes(String(deleteOrgConfirmText || "").trim().toLowerCase())
                }
              >
                {isDeletingOrg ? "Deleting..." : "Delete Organization"}
              </button>
            </div>
          )}
        >
          <div className="dev-panel-note">
            <strong>This will hide and mark deleted</strong>
            <p>{deleteOrgTarget.org_name || deleteOrgTarget.org_id}</p>
          </div>
          <label className="dev-delete-confirm">
            <span>Confirm by typing the organization name</span>
            <input
              type="text"
              value={deleteOrgConfirmText}
              onChange={(event) => setDeleteOrgConfirmText(event.target.value)}
              placeholder={deleteOrgTarget.org_name || deleteOrgTarget.org_id}
            />
          </label>
        </ModalShell>
      ) : null}

      {isTrialOrgConfirmOpen ? (
        <ModalShell
          title="Confirm Trial Organization Change"
          subtitle="Enterprise safeguard: type the target organization name exactly before the change is applied."
          onClose={() => {
            setIsTrialOrgConfirmOpen(false);
            setPendingTrialOrgId("");
            setTrialOrgConfirmText("");
          }}
          footer={(
            <div className="dev-modal-footer-actions">
              <button
                type="button"
                className="dev-btn"
                onClick={() => {
                  setIsTrialOrgConfirmOpen(false);
                  setPendingTrialOrgId("");
                  setTrialOrgConfirmText("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dev-btn danger"
                onClick={confirmTrialOrgChange}
                disabled={isSavingTrialOrg || String(trialOrgConfirmText || "").trim().toLowerCase() !== String(selectedTrialOrgName || "").trim().toLowerCase()}
              >
                {isSavingTrialOrg ? "Saving..." : "Confirm Change"}
              </button>
            </div>
          )}
        >
          <div className="dev-trial-confirm-grid">
            <div className="dev-panel-note">
              <strong>Current trial organization</strong>
              <p>{currentTrialOrgName}</p>
            </div>
            <div className="dev-panel-note">
              <strong>Target trial organization</strong>
              <p>{selectedTrialOrgName || "No organization selected"}</p>
            </div>
          </div>
          <p className="dev-trial-confirm-warning">
            Changing this setting controls the default organization for future explore users. Type the exact target organization name to continue.
          </p>
          <label className="dev-delete-confirm">
            <span>Type the exact target organization name</span>
            <input
              type="text"
              value={trialOrgConfirmText}
              onChange={(event) => setTrialOrgConfirmText(event.target.value)}
              placeholder={selectedTrialOrgName}
            />
          </label>
        </ModalShell>
      ) : null}

      {isOrgAccessModalOpen && selectedUser ? (
        <ModalShell
          title={`Organization access - ${selectedUser.full_name || selectedUser.email}`}
          subtitle={selectedUserIsAdmin ? "Admin accounts already have access to every organization." : "Review the organizations this account can access."}
          onClose={() => setIsOrgAccessModalOpen(false)}
          wide
        >
          {!selectedUserIsAdmin ? (
            <div className="dev-org-access-actions">
              <div className="dev-org-access-action-block">
                <div className="dev-org-access-action-copy">
                  <span className="dev-org-access-label">Grant access</span>
                  <strong>Add organization</strong>
                  <p>Assign this user to one more approved organization.</p>
                </div>
                <div className="dev-org-access-action-row">
                  <select value={orgAccessAddId} onChange={(event) => setOrgAccessAddId(event.target.value)}>
                    <option value="">Select organization</option>
                    {availableOrgOptions.map((org) => (
                      <option key={org.org_id} value={org.org_id}>{org.org_name || org.org_id}</option>
                    ))}
                  </select>
                  <button type="button" className="dev-btn" onClick={onAddOrgAccess} disabled={!orgAccessAddId}>
                    <span className="dev-btn-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20">
                        <path d="M4 16.5V5.2l6-2.4 6 2.4v11.3" />
                        <path d="M2.8 16.5h14.4" />
                        <path d="M7 7h.1" />
                        <path d="M10 7h.1" />
                        <path d="M13 7h.1" />
                        <path d="M10 10.6v4" />
                        <path d="M8 12.6h4" />
                      </svg>
                    </span>
                    Add Organization
                  </button>
                </div>
              </div>

              <div className="dev-org-access-action-block">
                <div className="dev-org-access-action-copy">
                  <span className="dev-org-access-label">Revoke access</span>
                  <strong>Remove organization</strong>
                  <p>Remove direct access while preserving the user account.</p>
                </div>
                <div className="dev-org-access-action-row">
                  <select value={orgAccessRemoveId} onChange={(event) => setOrgAccessRemoveId(event.target.value)}>
                    <option value="">Select accessible organization</option>
                    {selectedApprovedAccess.filter((item) => String(item.org_id) !== sharedTrialOrgId).map((item) => (
                      <option key={item.org_id} value={item.org_id}>{item.org_name || item.org_id}</option>
                    ))}
                  </select>
                  <button type="button" className="dev-btn danger" onClick={onRemoveOrgAccess} disabled={!orgAccessRemoveId}>
                    <span className="dev-btn-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20">
                        <path d="M4 16.5V5.2l6-2.4 6 2.4v11.3" />
                        <path d="M2.8 16.5h14.4" />
                        <path d="M7 7h.1" />
                        <path d="M10 7h.1" />
                        <path d="M13 7h.1" />
                        <path d="M8 12.6h4" />
                      </svg>
                    </span>
                    Remove Organization
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          {selectedUserIsAdmin ? (
            <div className="dev-empty-state">Admin users already have access to all organizations, so no organization controls are needed.</div>
          ) : (
            <>
              <div className="dev-access-list-head">
                <div>
                  <span className="dev-org-access-label">Current access</span>
                  <strong>Organizations</strong>
                </div>
                <span className="dev-access-count">{selectedApprovedAccess.length} active</span>
              </div>
              <div className="dev-access-list premium-scroll modal-list">
                {selectedApprovedAccess.map((item) => (
                  <article key={`${item.org_id}-${item.org_name || ""}`} className="dev-access-card">
                    <div>
                      <strong>{item.org_name || item.org_id}</strong>
                      <p className="dev-muted-mini">{item.org_id}</p>
                    </div>
                    <div className="dev-access-controls">
                      {sharedTrialOrgId && String(item.org_id) === sharedTrialOrgId ? (
                        <span className="dev-pill accent">shared demo</span>
                      ) : (
                        <span className="dev-pill accent">accessible</span>
                      )}
                    </div>
                  </article>
                ))}
                {!selectedApprovedAccess.length ? (
                  <div className="dev-empty-state dev-org-access-empty">
                    <strong>No organization access records found.</strong>
                    <p>Use the grant access control above to assign an organization.</p>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </ModalShell>
      ) : null}
    </div>
  );
}
